import { FC, useLayoutEffect, useRef, useState } from 'react';
import styles from './bt-sparkline.module.scss';

type Props = {
  values: number[];
  min: number;
  max: number;
  gridLines?: number[];
  startLabel?: string;
  endLabel?: string;
  width?: number;
  height?: number;
  fluid?: boolean;
  /** One per value; when given, hovering a point shows its label above it. */
  labels?: string[];
};

const PADDING = 4;
const HIT_RADIUS = 10;

const BtSparkline: FC<Props> = ({
  values,
  min,
  max,
  gridLines = [],
  startLabel,
  endLabel,
  width = 160,
  height = 40,
  fluid = false,
  labels,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  // A fluid chart draws to its real width, so the line reaches the end label instead of stopping short.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!fluid || !element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setMeasuredWidth(Math.round(entry.contentRect.width) || null),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [fluid]);

  if (!values.length) return null;

  const drawWidth = fluid && measuredWidth ? measuredWidth : width;
  const range = max - min || 1;
  const yOf = (value: number) => {
    const clamped = Math.min(max, Math.max(min, value));
    return PADDING + ((max - clamped) / range) * (height - 2 * PADDING);
  };
  const step = values.length > 1 ? (drawWidth - 2 * PADDING) / (values.length - 1) : 0;
  const xOf = (index: number) => (values.length > 1 ? PADDING + index * step : drawWidth / 2);
  const points = values.map((value, index) => ({ x: xOf(index), y: yOf(value) }));
  const label = hovered !== null ? labels?.[hovered] : undefined;
  // The first and last tooltips hang inwards, so they stay over the chart.
  const tooltipAlign =
    values.length > 1 && hovered === 0
      ? styles.tooltipStart
      : values.length > 1 && hovered === values.length - 1
      ? styles.tooltipEnd
      : '';

  return (
    <div ref={ref} className={styles.sparkline} style={{ width: fluid ? '100%' : width }}>
      <svg
        width={fluid ? '100%' : width}
        height={height}
        viewBox={`0 0 ${drawWidth} ${height}`}
        preserveAspectRatio={fluid ? 'xMinYMid meet' : undefined}
        aria-hidden="true"
      >
        {gridLines.map((value, index) => (
          <line
            key={index}
            className={styles.grid}
            x1={0}
            x2={drawWidth}
            y1={yOf(value)}
            y2={yOf(value)}
          />
        ))}
        <polyline className={styles.line} points={points.map(p => `${p.x},${p.y}`).join(' ')} />
        {points.map(point => (
          <circle key={point.x} className={styles.point} cx={point.x} cy={point.y} r={2.5} />
        ))}
        {labels &&
          points.map((point, index) => (
            <circle
              key={`hit-${index}`}
              data-sparkline-hit=""
              className={styles.hit}
              cx={point.x}
              cy={point.y}
              r={HIT_RADIUS}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
      </svg>
      {label && hovered !== null && (
        <span
          data-sparkline-tooltip=""
          className={`${styles.tooltip} ${tooltipAlign}`}
          style={{ left: `${(points[hovered].x / drawWidth) * 100}%`, top: points[hovered].y }}
        >
          {label}
        </span>
      )}
      {(startLabel || endLabel) && (
        <div className={styles.labels}>
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      )}
    </div>
  );
};

export default BtSparkline;
